import React from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import {
  CheckCircleOutlined,
  SyncOutlined,
  MinusCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { PageHeader, Menu, Layout, Tag } from 'antd';
import { gql, useQuery } from '@apollo/client';
import { ProjectOutlined } from '@ant-design/icons';
import './Project.less';
import { containsOneOf } from '../../utils';
import { ENUM_PROJECT_STATUS_CONTROL_TYPE } from 'constants/form';
import { useLoading } from 'hooks';
import ErrorPage from 'components/layout/ErrorPage';
import FinanceData from './financeData/FinanceData';

const { Content } = Layout;
const GET_PROJECT_HEADER = gql`
  query GET_PROJECT_HEADER($projectId: ID!, $ids: [ID!], $status: Status) {
    projects(ids: $ids, status: $status) {
      nodes {
        title
        projectStatus {
          id
          name
          controlType
        }
        profile {
          workProgramme
        }
      }
    }
    myScopeTypesInProject(projectId: $projectId) {
      viewProject
      scopeTypes
    }
  }
`;

const ProjectStatus = ({ projectStatus }) => {
  if (!projectStatus) return null;
  const { name, controlType } = projectStatus;
  if (controlType === ENUM_PROJECT_STATUS_CONTROL_TYPE.IN_PROGRESS) {
    return (
      <Tag color="processing" icon={<SyncOutlined spin />}>
        {name}
      </Tag>
    );
  }
  if (controlType === ENUM_PROJECT_STATUS_CONTROL_TYPE.COMPLETED) {
    return (
      <Tag color="success" icon={<CheckCircleOutlined />}>
        {name}
      </Tag>
    );
  }
  if (
    controlType === ENUM_PROJECT_STATUS_CONTROL_TYPE.TERMINATED ||
    controlType === ENUM_PROJECT_STATUS_CONTROL_TYPE.WITHDRAWN
  ) {
    return (
      <Tag color="default" icon={<MinusCircleOutlined />}>
        {name}
      </Tag>
    );
  }
  if (controlType === ENUM_PROJECT_STATUS_CONTROL_TYPE.PREPARATION) {
    return (
      <Tag color="default" icon={<ClockCircleOutlined />}>
        {name}
      </Tag>
    );
  }
};
const ProjectHeader = ({ children }) => {
  const { projectId: id } = useParams();
  const { pathname } = useLocation();
  const { data, loading } = useQuery(GET_PROJECT_HEADER, {
    variables: { projectId: id, ids: [id], status: 'ALL' },
  });
  const hasExpectedScopeTypes = (expectedScopeTypes) =>
    containsOneOf(expectedScopeTypes)(data?.myScopeTypesInProject?.scopeTypes);
  useLoading(loading);

  if (loading || !data) return <></>;
  if (data?.projects?.nodes.length === 0) {
    return (
      <ErrorPage
        errorCode={404}
        title="Project not found"
        subTitle="Sorry, the project you visited does not exist."
      />
    );
  }

  return (
    <Layout>
      <PageHeader
        title={data.projects?.nodes[0].title}
        subTitle={
          <>
            {data.projects?.nodes[0]?.profile?.workProgramme}
            <ProjectStatus
              projectStatus={data.projects?.nodes[0]?.projectStatus}
            />
          </>
        }
        className="project-header"
        avatar={{
          size: 'large',
          icon: <ProjectOutlined />,
        }}
      >
        <Menu
          mode="horizontal"
          selectedKeys={pathname.split('/')[3]}
          style={{ backgroundColor: 'inherit' }}
          className="project-header-menu"
        >
          <Menu.Item key="profile">
            <Link to={`/projects/${id}/profile`}>Profile</Link>
          </Menu.Item>
          {hasExpectedScopeTypes(['VIEW_TEAM']) && (
            <Menu.Item key="team">
              <Link to={`/projects/${id}/team`}>Team</Link>
            </Menu.Item>
          )}
          {hasExpectedScopeTypes(['VIEW_BUDGET', 'VIEW_VIREMENT']) && (
            <Menu.Item key="budget-virement">
              <Link to={`/projects/${id}/budget-virement`}>
                Budget &amp; Virement
              </Link>
            </Menu.Item>
          )}
          {hasExpectedScopeTypes(['VIEW_REPORT_SUBMISSION']) && (
            <Menu.Item key="report-submissions">
              <Link to={`/projects/${id}/report-submissions`}>
                Report Submission
              </Link>
            </Menu.Item>
          )}
          {hasExpectedScopeTypes(['VIEW_PROPOSAL_REVISION']) && (
            <Menu.Item key="proposal-revisions">
              <Link to={`/projects/${id}/proposal-revisions`}>
                Proposal Revision
              </Link>
            </Menu.Item>
          )}
          {hasExpectedScopeTypes(['VIEW_EXTENSION']) && (
            <Menu.Item key="extensions">
              <Link to={`/projects/${id}/extensions`}>Extension</Link>
            </Menu.Item>
          )}
          {hasExpectedScopeTypes(['VIEW_WITHDRAWAL']) && (
            <Menu.Item key="withdrawals">
              <Link to={`/projects/${id}/withdrawals`}>Withdrawal</Link>
            </Menu.Item>
          )}
          {hasExpectedScopeTypes(['VIEW_TERMINATION']) && (
            <Menu.Item key="termination">
              <Link to={`/projects/${id}/termination`}>Termination</Link>
            </Menu.Item>
          )}
          <Menu.Item key="finance-data">
            <FinanceData />
          </Menu.Item>
        </Menu>
      </PageHeader>
      <Content className="project-main-content">
        <div style={{ maxWidth: '1706px', margin: '0 auto' }}>{children}</div>
      </Content>
    </Layout>
  );
};

export default ProjectHeader;
